export const VALIDITY_PRESET_DAYS = [30, 60, 90, 180, 365] as const
export type ValidityPresetDays = (typeof VALIDITY_PRESET_DAYS)[number]
export const DEFAULT_VALIDITY_DAYS: ValidityPresetDays = 60

export function formatDateInputValue(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function parseDateInputValue(value: string): Date | null {
  if (!value) return null
  const parts = value.split('-').map(Number)
  if (parts.length !== 3) return null
  const [y, m, d] = parts
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

export function todayDateInputValue(): string {
  return formatDateInputValue(new Date())
}

/** Jobs toolbar date control: "Today" / "Hoy" or "Wed, Aug 12". */
export function formatDayNavLabel(
  ymd: string,
  todayYmd = todayDateInputValue(),
  options?: { locale?: string; todayLabel?: string }
): string {
  if (ymd === todayYmd) return options?.todayLabel ?? 'Today'
  const d = parseDateInputValue(ymd)
  if (!d) return ymd
  const intl = options?.locale === 'es' ? 'es-US' : 'en-US'
  return d.toLocaleDateString(intl, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

export function addCalendarDays(date: Date | string, days: number): string {
  const d =
    typeof date === 'string' ? parseDateInputValue(date) : new Date(date.getTime())
  if (!d) return ''
  d.setDate(d.getDate() + days)
  return formatDateInputValue(d)
}

export function daysBetween(start: Date | string, end: Date | string): number | null {
  const s = typeof start === 'string' ? parseDateInputValue(start) : start
  const e = typeof end === 'string' ? parseDateInputValue(end) : end
  if (!s || !e) return null
  const utcS = Date.UTC(s.getFullYear(), s.getMonth(), s.getDate())
  const utcE = Date.UTC(e.getFullYear(), e.getMonth(), e.getDate())
  return Math.round((utcE - utcS) / (1000 * 60 * 60 * 24))
}

export function inferValidityPreset(
  validUntil: string,
  anchorDate: string
): ValidityPresetDays | 'custom' {
  const days = daysBetween(anchorDate, validUntil)
  if (days === null) return DEFAULT_VALIDITY_DAYS
  if ((VALIDITY_PRESET_DAYS as readonly number[]).includes(days)) {
    return days as ValidityPresetDays
  }
  return 'custom'
}

export function defaultValidUntil(anchorDate?: string): string {
  return addCalendarDays(anchorDate || todayDateInputValue(), DEFAULT_VALIDITY_DAYS)
}
